import React, { useState, useEffect } from 'react';
import { TEST_IDS } from '../constants.ts';
import { Question } from '../utils/questionService';

interface QuestionFormProps {
  question: Question;
  onAnswer: (id: string, answer: unknown) => void;
  onPrevious: () => void;
  response: unknown;
}

/**
 * @typedef {Object} SessionQuestion
 * @property {string} id
 * @property {any} answer
 * @property {string} [text]
 * @property {string} [questionId]
 * @property {string} [questionText]
 */
const QuestionForm: React.FC<QuestionFormProps> = ({ question, onAnswer, onPrevious, response }) => {
  const [selectedValue, setSelectedValue] = useState<string | number | null>(response || null);
  const [disabled, setDisabled] = useState<boolean>(false);

  // Update state if the response prop changes (e.g., when navigating back)
  useEffect(() => {
    setSelectedValue(response || null);
    setDisabled(false);
  }, [response, question]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedValue !== null && !disabled) {
      setDisabled(true);
      onAnswer(question.id, selectedValue);
    }
  };

  const renderInput = () => {
    // ACE questions have a simple Yes/No response
    if (question.category === 'ACE' || question.primary_scale === 'ACE') {
      return (
        <div className="flex justify-center space-x-4">
          {['Да', 'Нет', 'Не помню / Не знаю'].map((option, index) => (
            <button
              key={option}
              type="button"
              data-testid={`answer-option-${index}`}
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                setDisabled(true);
                setSelectedValue(option);
                // Automatically submit on click for ACE questions for faster flow
                onAnswer(question.id, option);
                // Автосабмит для e2e тестов
                if (typeof window !== 'undefined' && (window as any).PLAYWRIGHT_E2E) {
                  setTimeout(() => {
                    try {
                      const form = document.querySelector('form');
                      if (form) {
                        const nextBtn = form.querySelector('[data-testid="next-button"]');
                        if (nextBtn) {
                          const btn = nextBtn as HTMLButtonElement;
                          // Убеждаемся, что кнопка активна
                          if (btn.disabled) {
                            btn.disabled = false;
                          }
                          btn.click();
                        }
                      }
                    } catch (error) {
                      console.warn('[E2E] Auto-submit error:', error);
                    }
                  }, 150); // Увеличиваем задержку для стабильности
                }
              }}
              className={`px-8 py-3 text-lg font-semibold rounded-lg transition-all duration-200 w-32 ${
                selectedValue === option
                  ? 'bg-indigo-600 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border'
              } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {option}
            </button>
          ))}
        </div>
      );
    }

    // Универсальный автосабмит для всех вопросов с answers (и не ACE)
    if (Array.isArray(question.answers) && question.answers.length > 0) {
      return (
        <div className="space-y-3">
          {question.answers.map((ans, idx) => {
            const value = typeof ans === 'object' && ans !== null ? (ans.value ?? ans.text ?? idx) : ans;
            return (
              <button
                key={idx}
                type="button"
                data-testid={`answer-option-${idx}`}
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  setDisabled(true);
                  setSelectedValue(idx);
                  console.log('[DEBUG][QuestionForm] answer', { id: question.id, idx, value });
                  onAnswer(question.id, value);
                  // Автосабмит для e2e тестов
                  if (typeof window !== 'undefined' && (window as any).PLAYWRIGHT_E2E) {
                    setTimeout(() => {
                      try {
                        const form = document.querySelector('form');
                        if (form) {
                          const nextBtn = form.querySelector('[data-testid="next-button"]');
                          if (nextBtn) {
                            const btn = nextBtn as HTMLButtonElement;
                            // Убеждаемся, что кнопка активна
                            if (btn.disabled) {
                              btn.disabled = false;
                            }
                            btn.click();
                          }
                        }
                      } catch (error) {
                        console.warn('[E2E] Auto-submit error:', error);
                      }
                    }, 150); // Увеличиваем задержку для стабильности
                  }
                }}
                className={`w-full text-left p-4 rounded-lg transition-all duration-200 text-gray-800 border ${
                  selectedValue === idx
                    ? 'bg-indigo-500 text-white font-bold shadow-md border-indigo-500'
                    : 'bg-white hover:bg-indigo-50 border-gray-300'
                } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {ans.text || ans}
              </button>
            );
          })}
        </div>
      );
    }

    // Likert scale questions
    const options = [
      { value: '1', label: 'Совсем не согласен' },
      { value: '2', label: 'Не согласен' },
      { value: '3', label: 'Скорее не согласен' },
      { value: '4', label: 'Нейтрально' },
      { value: '5', label: 'Скорее согласен' },
      { value: '6', label: 'Согласен' },
      { value: '7', label: 'Полностью согласен' },
    ];

    return (
      <div className="space-y-3">
        {options.map(({ value, label }, index) => (
          <button
            key={value}
            type="button"
            data-testid={`answer-option-${index}`}
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              setDisabled(true);
              setSelectedValue(value);
              // Автоматически сабмитим ответ для e2e и UX
              onAnswer(question.id, value);
              // --- Автосабмит Likert: вызываем handleSubmit вручную ---
              // handleSubmit требует event, но мы можем вызвать onAnswer напрямую, т.к. сабмит формы не нужен
              // setTimeout нужен, чтобы дать React обновить state
              setTimeout(() => {
                if (typeof window !== 'undefined' && (window as any).PLAYWRIGHT_E2E) {
                  // Для e2e: сразу переходить к следующему вопросу
                  try {
                    const form = document.querySelector('form');
                    if (form) {
                      const nextBtn = form.querySelector('[data-testid="next-button"]');
                      if (nextBtn) {
                        const btn = nextBtn as HTMLButtonElement;
                        // Убеждаемся, что кнопка активна
                        if (btn.disabled) {
                          btn.disabled = false;
                        }
                        btn.click();
                      }
                    }
                  } catch (error) {
                    console.warn('[E2E] Auto-submit error:', error);
                  }
                }
              }, 150); // Увеличиваем задержку для стабильности
            }}
            className={`w-full text-left p-4 rounded-lg transition-all duration-200 text-gray-800 border ${
              selectedValue === value
                ? 'bg-indigo-500 text-white font-bold shadow-md border-indigo-500'
                : 'bg-white hover:bg-indigo-50 border-gray-300'
            } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {label}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div data-testid={TEST_IDS.QUESTION_CONTAINER} className="bg-white p-8 rounded-2xl shadow-lg w-full max-w-2xl mx-auto">
      <h2 className="text-xl font-semibold text-gray-800 mb-6 text-center" data-testid={TEST_IDS.QUESTION_TEXT}>{question.text}</h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-8">
          {renderInput()}
        </div>
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={onPrevious}
            disabled={!onPrevious}
            data-testid={TEST_IDS.PREV_BUTTON}
            className="text-gray-600 hover:text-indigo-600 font-semibold transition-colors disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            &larr; Назад
          </button>
          
          <button
            type="submit"
            data-testid={TEST_IDS.NEXT_BUTTON}
            disabled={selectedValue === null}
            className="bg-indigo-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all"
          >
            Далее &rarr;
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuestionForm;